import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, Space, message, Input, Popconfirm, Tag, Typography } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import api from '../../api';

const { Title } = Typography;

const PurchaseOrders = () => {
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();

  const fetchPurchaseOrders = useCallback(async (page, pageSize, search) => {
    setLoading(true);
    try {
      const response = await api.get('/v1/purchase_orders', {
        params: {
          page,
          limit: pageSize,
          search: search,
        },
      });
      setPurchaseOrders(response.data.data.map(o => ({...o, key: o.id})));
      setPagination({
        current: response.data.page,
        pageSize: response.data.limit,
        total: response.data.total,
      });
    } catch (error) {
      message.error('获取采购订单列表失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPurchaseOrders(pagination.current, pagination.pageSize, searchTerm);
  }, [fetchPurchaseOrders, pagination.current, pagination.pageSize, searchTerm]);

  const handleTableChange = (newPagination) => {
    fetchPurchaseOrders(newPagination.current, newPagination.pageSize, searchTerm);
  };

  const handleSearch = (value) => {
    setSearchTerm(value);
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/v1/purchase_orders/${id}`);
      message.success('采购订单删除成功');
      if (purchaseOrders.length === 1 && pagination.current > 1) {
        fetchPurchaseOrders(pagination.current - 1, pagination.pageSize, searchTerm);
      } else {
        fetchPurchaseOrders(pagination.current, pagination.pageSize, searchTerm);
      }
    } catch (error) {
      message.error('删除采购订单失败');
    }
  };

  const columns = [
    { title: '订单号', dataIndex: 'order_no', key: 'order_no' },
    {
      title: '供应商',
      dataIndex: 'supplier',
      key: 'supplier',
      render: (supplier) => supplier?.name || 'N/A',
    },
    { title: '订单日期', dataIndex: 'order_date', key: 'order_date', render: (text) => text ? new Date(text).toLocaleDateString() : 'N/A' },
    { title: '总金额', dataIndex: 'total_amount', key: 'total_amount', render: (text) => `¥ ${parseFloat(text || 0).toFixed(2)}` },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        const statusMap = {
          pending: { text: '待入库', color: 'blue' },
          received: { text: '已入库', color: 'green' },
        };
        const statusInfo = statusMap[status] || { text: '未知', color: 'grey' };
        return <Tag color={statusInfo.color}>{statusInfo.text}</Tag>;
      }
    },
    { title: '更新时间', dataIndex: 'updated_at', key: 'updated_at' },
    {
      title: '操作',
      key: 'action',
      align: 'left',
      render: (_, record) => (
        <Space size="small" style={{ marginLeft: -8 }}>
            <Button type="link" size="small" onClick={() => navigate(`/procurement/purchase-orders/${record.id}`)}>
                {record.status === 'received' ? '查看' : '编辑'}
            </Button>
            {record.status !== 'received' && (
              <Popconfirm
                  title="确定删除此订单吗？"
                  onConfirm={() => handleDelete(record.id)}
                  okText="确定"
                  cancelText="取消"
              >
                  <Button type="link" size="small" danger>删除</Button>
              </Popconfirm>
            )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>采购订单管理</Title>
        <Space>
          <Input.Search
            placeholder="搜索订单号或供应商"
            onSearch={handleSearch}
            style={{ width: 250 }}
            allowClear
          />
          <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/procurement/purchase-orders/new')}>
            新增采购订单
          </Button>
        </Space>
      </div>

      <Table
        columns={columns}
        dataSource={purchaseOrders}
        rowKey="id"
        pagination={pagination}
        loading={loading}
        onChange={handleTableChange}
        size="small"
        bordered
      />
    </div>
  );
};

export default PurchaseOrders;